import { DestroyableTokenList } from '../types';

/**
 * Stores properties of TokenListWithCallbacks instances that should not be accessible to consumers
 */
interface PrivateProps {
    /**
     * Version of the list that has mutable properties
     */
    mutable: DestroyableTokenListWithCallbacks;
    /**
     * True length of the list
     */
    length: number;
    /**
     * True value of the list
     */
    value: string;
    /**
     * Called when tokens are added
     */
    onAdd?: (token: string) => void;
    /**
     * Called when tokens are removed
     */
    onRemove?: (token: string) => void;
    /**
     * Called when the list is destroyed
     */
    onDestroy?: () => void;
}

/**
 * Tracks the values of private properties of TokenListWithCallbacks instances.
 *
 * Properties that should not be visible in the public interface must be
 * stored here. Storing them in the mutable instance would cause them to be
 * readable in the public counterpart.
 *
 * These are never exposed outside this module.
 */
const privateProps = new WeakMap<DestroyableTokenListWithCallbacks, PrivateProps>();

/**
 * Tracks a set tokens with callbacks that trigger whenever the tokens change.
 *
 * This class's properties cannot be directly written or deleted due to an
 * internal abstraction layer, except for the `value` property, which is
 * directly writable (but not deletable). The only way for any other properties
 * to be added is for new numerically-indexed properties to appear as a result
 * of tokens added or removed by setting `.value`, or by calling `.add()`,
 * `.remove()`, `.replace()`, or `.toggle()`.
 *
 * When a new token is added to an instance by any valid means, the onAdd()
 * callback will be called once for each new token added. Adding any token
 * which was already present will not result in the callback being called.
 *
 * When any existing token is removed by any means other than total destruction
 * of the token list, the onRemove() callback will be called once for each token
 * that was removed. Removing tokens which were not present and destroying the
 * token list do not trigger onRemove().
 *
 * The token list can be destroyed by calling the .destroy() method. This
 * triggers the onDestroy() callback. Destroying the list does not
 * remove the tokens from the list per se, but will cause the token list as a
 * whole to become unusable by making most properties and methods trigger
 * errors upon access to them.
 *
 * The token list is not technically destroyed until after the onDestroy()
 * callback has returned. This means that the list is still accessible while
 * the onDestroy() callback is running, and tokens can be read, added, and
 * removed during this time. However, immediately following the call, the
 * token list is no longer considered valid and further use of it will result
 * in errors.
 */
export default class DestroyableTokenListWithCallbacks implements DestroyableTokenList {

    [index: number]: string;

    constructor(
        /**
         * Called when a new class is added to the list
         *
         * Also called when a class is replaced with another class which was
         * not previously in the list.
         *
         * Not called if an attempt is made to add a class which is already in
         * the list or if a class is being replaced with one already in the
         * list.
         */
        onAdd?: (token: string) => void,

        /**
         * Called when an existing class is removed from the list
         *
         * Also called when class is replaced with another class which was
         * not previously in the list, or if a class is replaced with one
         * already in the list but which is not the same as the one being
         * replaced.
         *
         * Not called if an attempt is made to remove a class that is not in
         * the list, or if an attempt is made to replace a class with itself.
         */
        onRemove?: (token: string) => void,

        /**
         * Called when the token list is destroyed.
         */
        onDestroy?: () => void
    ) {
        const immutable = Object.create(this) as this;
        Object.freeze(immutable);
        const props: PrivateProps = {
            length: 0,
            mutable: this,
            onAdd,
            onDestroy,
            onRemove,
            value: '',
        };
        privateProps.set(this, props);
        privateProps.set(immutable, props);
        return immutable;
    }

    /**
     * Returns the total number of unique tokens contained within the list
     *
     * Duplicate entries are not counted.
     */
    public get length() {
        return getPrivateProps(this).length;
    }

    /**
     * Adds all unique given tokens to the list
     *
     * If any duplicates exist in the final list state, they will be removed.
     * This includes any duplicates which may have already existed in the list
     * prior to the additions. (Removal of duplicates anywhere in the list does
     * not trigger onRemove().)
     *
     * `.value` will be updated to reflect changes, with new tokens being added
     * to the end of the string.
     *
     * If any unique token is in the list which was not present prior to calling
     * `.add()`, `.onAdd()` will be triggered for that token exactly once. It
     * will not be triggered for duplicates.
     *
     * @param tokens One or more tokens to add
     * @throws {SyntaxError} Thrown when called without tokens
     * @throws {Error} Thrown if any given token contains whitespace
     */
    public add(...tokens: string[]) {
        if (tokens.length === 0) {
            throw new SyntaxError('add() called without any tokens');
        }
        for (const [i, token] of Object.entries(tokens)) {
            if (/\s/.test(token)) {
                throw new Error(`Token ${i} ("${token}") contains whitespace`);
            }
        }
        const uniqueTokens = Array.from(new Set([...this.value.split(' '), ...tokens])).filter((t) => t);
        this.value = uniqueTokens.join(' ');
    }

    /**
     * Removes all given tokens from the list.
     *
     * If any duplicates exist in the final list state, they will be removed.
     * This occurs whether or not the input list contained any duplicates.
     * (Removal of duplicates does not trigger onRemove().)
     *
     * If any unique token that was present in the list is no longer present
     * after calling `.remove()`, `.onRemove()` will be triggered with that
     * token exactly once. It will not be triggered for duplicate instances.
     *
     * @param tokens Tokens to be removed
     * @throws {SyntaxError} Thrown when called without tokens
     * @throws {Error} Thrown if any given token contains whitespace
     */
    public remove(...tokens: string[]) {
        if (tokens.length === 0) {
            throw new SyntaxError('remove() called without any tokens');
        }
        for (const [i, token] of Object.entries(tokens)) {
            if (/\s/.test(token)) {
                throw new Error(`Token ${i} ("${token}") contains whitespace`);
            }
        }
        const current = Array.from(new Set(this.value.split(' '))).filter((t)=> t)
        const removed = Array.from(new Set(tokens)).filter((t) => t);
        this.value = current.filter((t) => !removed.includes(t)).join(' ');
    }

    public contains(token: string) {
        return Array.from(this).includes(token);
    }

    public forEach(callbackFn: (token: string, i: number, tokenList: this) => void) {
        for (let i = 0; i < this.length; i++) {
            callbackFn(this[i], i, this);
        }
    }

    public get value() {
        return getPrivateProps(this).value;
    }

    public set value(newValue: string) {
        const pProps = getPrivateProps(this);

        const oldValue = pProps.value;
        pProps.value = newValue;

        const oldTokens = Array.from(new Set(oldValue.split(' '))).filter((t) => t);
        for (let i = 0; i < this.length; i++) {
            delete pProps.mutable[i];
        }

        const newTokens = Array.from(new Set(newValue.split(' '))).filter((t) => t);
        for (let i = 0; i < newTokens.length; i++) {
            pProps.mutable[i] = newTokens[i];
        }

        pProps.length = newTokens.length;

        const removed = oldTokens.filter((t) => !newTokens.includes(t));
        for (const t of removed) {
            if (pProps.onRemove) {
                pProps.onRemove(t);
            }
        }

        const added = newTokens.filter((t) => !oldTokens.includes(t));
        for (const t of added) {
            if (pProps.onAdd) {
                pProps.onAdd(t);
            }
        }
    }

    public item(index: number) {
        return this[index];
    }

    /**
     * Replaces a token with another token
     *
     * This will trigger either nothing, onRemove(), or both onRemove() and
     * onAdd(), depending on the result of the operation.
     *
     * - If the old token was not present or if both the old and new tokens are
     *   the same, no action is taken and no callbacks are triggered.
     * - If the old token and new token are both already present, the old token
     *   is removed and the new token is not modified, which results in only
     *   onRemove() being triggered.
     * - If the old token is present and the new token is different from the
     *   old token and not already present, the old token will be removed and
     *   the new token will be added, which triggers onRemove() first, followed
     *   by onAdd().
     *
     * In all cases, the final state of the list will have already been applied
     * before any callbacks are triggered.
     *
     * @param oldToken Token to remove. May not be empty or contain whitespace.
     * @param newToken Token to add. May not be empty or contain whitespace.
     * @throws {Error|SyntaxError} Thrown if either oldToken or newToken is not a valid token value.
     * @return True if oldToken and newToken are diff
     */
    public replace(oldToken: string, newToken: string) {
        if (!oldToken) {
            throw new SyntaxError('oldToken is empty');
        } else if (/\s/.test(oldToken)) {
            throw new DOMException(`oldToken ("${oldToken}") contains whitespace`)
        } else if (!newToken) {
            throw new SyntaxError('newToken is empty');
        } else if (/\s/.test(newToken)) {
            throw new DOMException(`newToken ("${newToken}") contains whitespace`)
        } else if (!this.contains(oldToken)) {
            return false;
        } else if (newToken === oldToken) {
            return true;
        } else {
            this.remove(oldToken);
            this.add(newToken);
            return true;
        }
    }

    /**
     * @throws {TypeError} Thrown to mimic the behavior of Chromium-based web browsers
     */
    public supports(token: string): never {
        throw new TypeError('This error was thrown to mimic the behavior of Chromium-based web browsers.');
    }

    /**
     * Returns `.value`;
     */
    public toString() {
        return this.value;
    }

    /**
     * Either adds or removes a token depending on `force`.
     *
     * - If force is not passed or undefined, the given token will be added
     *   if it is not present or removed if it is present.
     * - If force is passed and is truthy, `.add()` is called with the given
     *   token.
     * - If forced is passed, is not `undefined`, and is falsy, `.remove()`
     *   is called with the given token.
     *
     * @param token Token to add or remove
     * @param force Modifies the behavior according to the rules above
     * @return Whether or not the token is present following this call
     */
    public toggle(token: string, force?: boolean) {
        if (force === undefined) {
            if (this.contains(token)) {
                this.remove(token);
            } else {
                this.add(token);
            }
        } else if (force) {
            this.add(token);
        } else {
            this.remove(token);
        }
        return this.contains(token);
    }

    /**
     * Destroys the token list
     *
     * This does NOT trigger onRemove().
     */
    public destroy() {
        const pProps = getPrivateProps(this);
        const onDestroy = pProps.onDestroy;
        if (onDestroy) {
            onDestroy();
        }
        privateProps.delete(this);
    }

    public *[Symbol.iterator]() {
        for (let i = 0; i < this.length; i ++) {
            yield this[i];
        }
    }
}

const getPrivateProps = (list: DestroyableTokenListWithCallbacks) => {
    const pProps = privateProps.get(list);
    if (!pProps) {
        throw new Error('Token list has been destroyed');
    } else {
        return pProps;
    }
};
